import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';

const Gstr2bHsnModule = ({ rowData }) => {
    const lsKeyAnnB = `annexure_b_new_${rowData?.client?.id || rowData?.id || 'default'}`;
    const [extensionId, setExtensionId] = useState(() => localStorage.getItem('GST_EXTENSION_ID') || '');
    const [extStatus, setExtStatus] = useState('checking');
    const [token, setToken] = useState('');
    const [tokenSaved, setTokenSaved] = useState(false);
    const [gstinText, setGstinText] = useState('');
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(null);
    const [rows, setRows] = useState([]);
    const [showInstructions, setShowInstructions] = useState(false);
    const [showTokenInstructions, setShowTokenInstructions] = useState(false);
    const [stats, setStats] = useState(null);
    const [copied, setCopied] = useState(false);

    const abortRef = useRef(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        checkExtension();
        // eslint-disable-next-line
    }, []);

    // Auto-pull missing GSTINs from Annexure B
    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKeyAnnB);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.dataRows && parsed.dataRows.length > 0) {
                    const uniqueGstins = new Set();
                    parsed.dataRows.forEach(r => {
                        const g = (r.gstin || "").trim();
                        // Only auto-extract valid length GSTINs that lack an HSN code
                        if (g && g.length >= 14 && !r.hsn) {
                            uniqueGstins.add(g);
                        }
                    });
                    if (uniqueGstins.size > 0 && !gstinText) {
                        setGstinText(Array.from(uniqueGstins).join('\n'));
                    }
                }
            }
        } catch (e) {
            console.error("Failed to parse Annexure B data automatically", e);
        }
    }, [lsKeyAnnB, gstinText]);

    function sendToExtension(message) {
        return new Promise((resolve, reject) => {
            if (!window.chrome?.runtime?.sendMessage) {
                reject(new Error('Chrome extension API not available'));
                return;
            }
            if (!extensionId || extensionId.trim().length === 0) {
                reject(new Error('Please enter Extension ID first'));
                return;
            }
            window.chrome.runtime.sendMessage(extensionId.trim(), message, (response) => {
                if (window.chrome.runtime.lastError) {
                    reject(new Error(window.chrome.runtime.lastError.message));
                    return;
                }
                resolve(response);
            });
        });
    }

    async function checkExtension() {
        setExtStatus('checking');
        try {
            const res = await sendToExtension({ type: 'GST_PING' });
            if (res?.status === 'ok') {
                setExtStatus('connected');
                loadToken();
            } else {
                setExtStatus('missing');
            }
        } catch (e) {
            setExtStatus('missing');
        }
    }

    async function loadToken() {
        try {
            const res = await sendToExtension({ type: 'GST_GET_TOKEN' });
            if (res?.token) {
                setToken(res.token);
                setTokenSaved(true);
            }
        } catch (e) {
            console.log('Failed to load token:', e);
        }
    }

    async function handleSaveToken() {
        try {
            await sendToExtension({ type: 'GST_SAVE_TOKEN', token });
            setTokenSaved(true);
            setTimeout(() => setTokenSaved(false), 2000);
        } catch (e) {
            alert("Failed to save token to extension: " + e.message);
        }
    }

    const handleSaveExtId = () => {
        const val = extensionId.trim();
        localStorage.setItem('GST_EXTENSION_ID', val);
        checkExtension();
    };

    function validGSTIN(g) {
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g);
    }

    const updateRow = (gstin, status, code = '', codeSource = '') => {
        setRows(prev => {
            const existing = [...prev];
            const idx = existing.findIndex(r => r.gstin === gstin);
            if (idx >= 0) {
                existing[idx] = { ...existing[idx], status, code, codeSource };
            } else {
                existing.push({ gstin, status, code, codeSource });
            }
            return existing;
        });
    };

    async function runLookup() {
        const list = gstinText.split('\n').map(s => s.trim()).filter(Boolean);
        if (!list.length) return;

        abortRef.current = false;
        setRunning(true);
        setStats(null);
        setProgress(null);

        const initialRows = list.map(g => ({ gstin: g, code: '', codeSource: '', status: 'pending' }));
        setRows(initialRows);

        const results = [];
        let foundCount = 0;
        let notfoundCount = 0;
        let errorCount = 0;

        for (let i = 0; i < list.length; i++) {
            if (abortRef.current) break;

            const gstin = list[i].toUpperCase();
            if (!gstin) continue;

            setProgress({ current: i + 1, total: list.length, gstin });
            updateRow(gstin, 'pending');

            if (!validGSTIN(gstin)) {
                updateRow(gstin, 'invalid');
                results.push({ gstin, code: '', status: 'invalid' });
                errorCount++;
                continue;
            }

            try {
                const res = await sendToExtension({
                    type: 'GST_HSN_LOOKUP',
                    gstins: [gstin]
                });

                if (res?.error === 'TOKEN_EXPIRED') {
                    updateRow(gstin, 'error', 'Token Expired');
                    errorCount++;
                    alert('⚠ Token expired. Get a fresh token from F12 → Network.');
                    break;
                }

                if (res?.error === 'NO_GST_TAB') {
                    updateRow(gstin, 'error', 'No GST Tab');
                    errorCount++;
                    alert('GST portal tab not found. Open services.gst.gov.in and login.');
                    break;
                }

                const row = res?.results?.[0];
                if (row) {
                    updateRow(gstin, row.status, row.code, row.codeSource || '');
                    results.push(row);
                    if (row.status === 'found') foundCount++;
                    else if (row.status === 'notfound') notfoundCount++;
                    else errorCount++;
                } else {
                    updateRow(gstin, 'error', res?.error || 'Unknown error');
                    results.push({ gstin, code: '', codeSource: '', status: 'error' });
                    errorCount++;
                }

            } catch (e) {
                updateRow(gstin, 'error', e.message);
                results.push({ gstin, code: '', status: 'error' });
                errorCount++;
            }
        }

        setStats({
            total: list.length,
            found: foundCount,
            notfound: notfoundCount,
            errors: errorCount
        });
        setRunning(false);
        setProgress(null);

        if (results.length > 0) {
            syncToAnnexureB(results);
        }
    }

    // Determine category based on codeSource and code prefix
    // codeSource = 'sac' → Input Services, 'hsn' → Inputs
    // Fallback: code starts with 99 or 00 → Input Services, else Inputs
    const determineHsnType = (code, codeSource) => {
        if (!code) return "Inputs";
        const c = String(code).trim();
        // If codeSource is known, use it directly
        if (codeSource === 'sac') return "Input Services";
        if (codeSource === 'hsn') return "Inputs";
        // Fallback: determine from code prefix
        if (c.startsWith("99") || c.startsWith("00")) return "Input Services";
        return "Inputs";
    };

    const syncToAnnexureB = (fetchedResults, opts = {}) => {
        const force = !!opts.force; // manual corrections overwrite existing HSN
        try {
            const saved = localStorage.getItem(lsKeyAnnB);
            if (!saved) return;
            const parsed = JSON.parse(saved);
            
            // Build map: GSTIN → { code, codeSource }
            const gstinMap = new Map();
            fetchedResults.forEach(r => {
                if (r.status === 'found' && r.code) {
                    gstinMap.set(r.gstin, { code: r.code, codeSource: r.codeSource || '' });
                }
            });

            if (gstinMap.size === 0) return;

            let updatedCount = 0;
            if (parsed.dataRows) {
                parsed.dataRows.forEach(row => {
                    const g = (row.gstin || "").trim();

                    // Blank GSTIN → default HSN 9965 + Input Services (except for Imports)
                    if (!g && !row.hsn) {
                        if (row.inwardType === "Import of Goods" || row.inwardType === "Import of Services") {
                            return; // Do not apply 9965 for Imports; leave it blank for manual entry
                        }
                        row.hsn = "9965";
                        if (row.category !== 'Capital Goods') {
                            row.category = "Input Services";
                        }
                        updatedCount++;
                        return;
                    }

                    const fetched = gstinMap.get(g);
                    if (fetched && (force || !row.hsn)) {
                        row.hsn = fetched.code;
                        if (row.category !== 'Capital Goods') {
                            row.category = determineHsnType(fetched.code, fetched.codeSource);
                        }
                        updatedCount++;
                    }
                });
                if (updatedCount > 0) {
                    localStorage.setItem(lsKeyAnnB, JSON.stringify(parsed));
                    alert(`✅ Auto-synced ${updatedCount} rows in Annexure B with new HSN codes!`);
                }
            }
        } catch (e) {
            console.error("Failed to sync back to Annexure B", e);
        }
    };

    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer);
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws, { defval: "" });

            const fetchedResults = [];
            let validCount = 0;

            data.forEach(row => {
                let gstin = '';
                let hsn = '';

                for (let key in row) {
                    const upperKey = key.toUpperCase();
                    const val = String(row[key]).trim();
                    if (!val) continue;

                    if (upperKey.includes('GST') && validGSTIN(val)) {
                        gstin = val;
                    } else if (upperKey.includes('HSN') || upperKey.includes('SAC')) {
                        hsn = val;
                    }
                }

                if (!gstin || !hsn) {
                    for (let key in row) {
                        const val = String(row[key]).trim();
                        if (!val) continue;
                        if (!gstin && validGSTIN(val)) {
                            gstin = val;
                        } else if (!hsn && /^[0-9]{4,8}$/.test(val)) {
                            hsn = val;
                        }
                    }
                }

                if (gstin && hsn) {
                    fetchedResults.push({ gstin, code: hsn, status: 'found' });
                    validCount++;
                    updateRow(gstin, 'found', hsn);
                }
            });

            if (fetchedResults.length > 0) {
                syncToAnnexureB(fetchedResults);
                alert(`✅ Read ${validCount} valid GSTIN & HSN pairs from Excel and synced to Annexure B!`);
            } else {
                alert('❌ No valid GSTIN and HSN pairs found in the uploaded Excel.');
            }
        } catch (err) {
            console.error("Excel processing failed", err);
            alert("Error processing Excel: " + err.message);
        }

        e.target.value = '';
    };

    const handleStop = () => {
        abortRef.current = true;
    };

    const handleCopy = () => {
        const header = "GSTIN\tHSN_SAC\tStatus";
        const body = rows.map(r => `${r.gstin}\t${r.code}\t${r.status}`).join('\n');
        navigator.clipboard.writeText(header + '\n' + body);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClear = () => {
        setGstinText('');
        setRows([]);
        setStats(null);
        setProgress(null);
        abortRef.current = true;
        setRunning(false);
    };

    // Manual correction from the results table → overwrite in Annexure B.
    // Used for the rare cases where the portal only returns a 2-digit code.
    const applyManualCode = (gstin, newCode) => {
        const code = String(newCode || '').trim();
        setRows(prev => prev.map(r =>
            r.gstin === gstin
                ? { ...r, code, status: code ? 'found' : r.status }
                : r
        ));
        if (!code) return;
        const codeSource = (code.startsWith('99') || code.startsWith('00')) ? 'sac' : 'hsn';
        syncToAnnexureB([{ gstin, code, codeSource, status: 'found' }], { force: true });
    };

    const lineCount = gstinText.split('\n').filter(s => s.trim()).length;

    return (
        <div className="hsn-dark-tab">
            <style>{`
                .hsn-dark-tab {
                    background: #0b0f1a;
                    color: #f8fafc;
                    padding: 24px;
                    border-radius: 12px;
                    font-family: 'DM Mono', monospace;
                }
                .hsn-card {
                    background: #111827;
                    border: 1px solid #1e293b;
                    border-radius: 8px;
                    padding: 20px;
                    margin-bottom: 24px;
                }
                .syne-title {
                    font-family: 'Syne', sans-serif;
                    font-weight: 700;
                    margin-bottom: 16px;
                    color: #f8fafc;
                    font-size: 18px;
                }
                .status-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    border-radius: 9999px;
                    font-weight: bold;
                    font-size: 14px;
                }
                .chip-checking { background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid #f59e0b; }
                .chip-connected { background: rgba(74, 222, 128, 0.1); color: #4ade80; border: 1px solid #4ade80; }
                .chip-missing { background: rgba(251, 113, 133, 0.1); color: #fb7185; border: 1px solid #fb7185; }
                
                .token-input {
                    background: #0b0f1a;
                    border: 1px solid #1e293b;
                    color: #f8fafc;
                    padding: 10px 16px;
                    border-radius: 6px;
                    font-family: 'DM Mono', monospace;
                    width: 320px;
                    margin-right: 12px;
                }
                .token-input:focus { outline: none; border-color: #60a5fa; }
                .token-input::placeholder { color: #475569; }
                
                .hsn-btn {
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: bold;
                    border: none;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: 0.2s;
                    font-family: 'DM Mono', monospace;
                }
                .btn-amber { background: #f59e0b; color: #111827; }
                .btn-amber:hover { background: #d97706; }
                .btn-blue { background: #60a5fa; color: #0b0f1a; }
                .btn-blue:hover { background: #3b82f6; }
                .btn-rose { background: #fb7185; color: #111827; }
                .btn-rose:hover { background: #e11d48; }
                .btn-outline { background: transparent; border: 1px solid #475569; color: #94a3b8; }
                .btn-outline:hover { background: #1e293b; color: #f8fafc; }
                
                .gstin-textarea {
                    width: 100%;
                    height: 150px;
                    background: #0b0f1a;
                    border: 1px solid #1e293b;
                    color: #f8fafc;
                    padding: 16px;
                    border-radius: 8px;
                    font-family: 'DM Mono', monospace;
                    resize: vertical;
                    margin-bottom: 16px;
                }
                .gstin-textarea:focus { outline: none; border-color: #f59e0b; }
                .gstin-textarea::placeholder { color: #475569; }
                
                .hsn-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 16px;
                    font-size: 14px;
                }
                .hsn-table th {
                    background: #1e293b;
                    color: #94a3b8;
                    padding: 12px;
                    text-align: left;
                    font-weight: normal;
                }
                .hsn-table td {
                    padding: 12px;
                    border-bottom: 1px solid #1e293b;
                    color: #f8fafc;
                }
                
                .stat-box {
                    background: #0b0f1a;
                    border: 1px solid #1e293b;
                    border-radius: 8px;
                    padding: 16px;
                    text-align: center;
                    flex: 1;
                }
                .stat-num { font-size: 24px; font-weight: bold; margin-bottom: 4px; }
                
                .color-found { color: #4ade80; }
                .color-notfound { color: #f59e0b; }
                .color-error { color: #fb7185; }
                .color-pending { color: #94a3b8; }
                
                .prog-bar-bg { height: 6px; background: #1e293b; border-radius: 3px; overflow: hidden; margin-bottom: 16px; }
                .prog-bar-fill { height: 100%; background: #f59e0b; transition: width 0.3s; }

                .stagger-row { animation: fadeIn 0.3s ease-in forwards; opacity: 0; }
                @keyframes fadeIn { to { opacity: 1; } }

                .code-edit {
                    background: #0b0f1a;
                    border: 1px solid #1e293b;
                    color: inherit;
                    font-family: 'DM Mono', monospace;
                    font-weight: bold;
                    font-size: 14px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    width: 120px;
                }
                .code-edit:focus { outline: none; border-color: #f59e0b; }
                .code-edit::placeholder { color: #475569; font-weight: normal; }
            `}</style>

            {/* SECTION 1 - Extension Status Card */}
            <div className="hsn-card">
                <div className="syne-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    Extension Status
                    <button className="hsn-btn btn-outline" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={checkExtension}>
                        Recheck
                    </button>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>Extension ID</span>
                    <input 
                        type="text" 
                        value={extensionId} 
                        onChange={e => setExtensionId(e.target.value)} 
                        className="token-input"
                        style={{ width: '380px' }}
                        placeholder="e.g. egnmmjcnipmpnlhheffmglphekaepnfg" 
                    />
                    <button className="hsn-btn btn-blue" onClick={handleSaveExtId}>Save & Connect</button>
                </div>
                
                {extStatus === 'checking' && (
                    <div className="status-chip chip-checking">⟳ Checking extension...</div>
                )}
                {extStatus === 'connected' && (
                    <div className="status-chip chip-connected">✓ Extension connected — GST portal bridge ready</div>
                )}
                {extStatus === 'missing' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
                        <div className="status-chip chip-missing">✗ Extension not found — Check ID or Install Bridge</div>
                        {!showInstructions ? (
                            <button className="hsn-btn btn-outline" onClick={() => setShowInstructions(true)}>Show Install Instructions</button>
                        ) : (
                            <div style={{ background: '#0b0f1a', padding: '16px', borderRadius: '8px', border: '1px solid #1e293b', width: '100%' }}>
                                <ol style={{ margin: 0, paddingLeft: '20px', color: '#94a3b8', lineHeight: '1.8' }}>
                                    <li>Download and extract the extension ZIP</li>
                                    <li>Chrome &rarr; <span style={{ color: '#f8fafc' }}>chrome://extensions</span> &rarr; Developer mode ON</li>
                                    <li>Load unpacked &rarr; select folder</li>
                                    <li>Copy the assigned ID and paste it in the box above!</li>
                                    <li>Open <span style={{ color: '#f8fafc' }}>services.gst.gov.in</span> and login</li>
                                </ol>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* SECTION 2 - Token Management */}
            <div className="hsn-card">
                <div className="syne-title">Token Management</div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>AT Token</span>
                    <input 
                        type="password" 
                        value={token} 
                        onChange={e => setToken(e.target.value)} 
                        className="token-input"
                        placeholder={tokenSaved ? "••••••••" : "Paste token here"} 
                    />
                    <button className="hsn-btn btn-blue" onClick={handleSaveToken}>Save Token</button>
                    {tokenSaved ? (
                        <span style={{ color: '#4ade80', fontSize: '14px' }}>✅ Token active</span>
                    ) : (
                        <span style={{ color: '#f59e0b', fontSize: '14px' }}>⚠ No token saved</span>
                    )}
                </div>

                <div style={{ marginTop: '12px' }}>
                    <button 
                        className="hsn-btn btn-outline" 
                        style={{ padding: '4px 12px', fontSize: '12px' }} 
                        onClick={() => setShowTokenInstructions(!showTokenInstructions)}
                    >
                        {showTokenInstructions ? 'Hide Instructions' : 'How to get token?'}
                    </button>
                    {showTokenInstructions && (
                        <div style={{ background: '#0b0f1a', padding: '12px', borderRadius: '8px', border: '1px solid #1e293b', marginTop: '12px' }}>
                            <ol style={{ margin: 0, paddingLeft: '20px', color: '#94a3b8', fontSize: '13px', lineHeight: '1.6' }}>
                                <li>Go to GST portal tab (already open)</li>
                                <li>Press F12 &rarr; Network tab</li>
                                <li>Do any GSTIN search on portal</li>
                                <li>Click: <span style={{ color: '#60a5fa' }}>goodservice?gstin=...</span> request</li>
                                <li>Headers &rarr; Request Headers &rarr; copy "at:" value</li>
                            </ol>
                        </div>
                    )}
                </div>
            </div>

            {/* SECTION 3 - GSTIN Input + Lookup */}
            <div className="hsn-card">
                <div className="syne-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    GSTIN Input + Lookup
                    <span style={{ background: '#1e293b', color: '#94a3b8', padding: '4px 12px', borderRadius: '4px', fontSize: '12px' }}>
                        {lineCount} GSTINs
                    </span>
                </div>
                
                <textarea 
                    className="gstin-textarea" 
                    placeholder="27AAAAA0000A1Z5&#10;29BBBBB1111B2Z6...&#10;&#10;If Annexure B has missing HSN codes, GSTINs auto-appear here!"
                    value={gstinText}
                    onChange={e => setGstinText(e.target.value)}
                    disabled={running}
                />

                {running && progress && (
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '12px', marginBottom: '8px' }}>
                            <span>Processing: <span style={{ color: '#f8fafc' }}>{progress.gstin}</span></span>
                            <span>{progress.current} / {progress.total}</span>
                        </div>
                        <div className="prog-bar-bg">
                            <div className="prog-bar-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }}></div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {!running ? (
                        <button className="hsn-btn btn-amber" onClick={runLookup} disabled={lineCount === 0 || extStatus !== 'connected'}>
                            ▶ Start Lookup
                        </button>
                    ) : (
                        <button className="hsn-btn btn-rose" onClick={handleStop}>
                            ⏹ Stop
                        </button>
                    )}
                    <button className="hsn-btn btn-outline" onClick={handleCopy} disabled={rows.length === 0}>
                        ⎘ {copied ? 'Copied ✓' : 'Copy TSV'}
                    </button>
                    <button className="hsn-btn btn-outline" onClick={handleClear}>
                        ✕ Clear
                    </button>
                    <button className="hsn-btn btn-blue" onClick={() => fileInputRef.current?.click()} title="Upload Excel with GSTIN & HSN columns">
                        📁 Manual Upload Fallback
                    </button>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        accept=".xlsx, .xls, .csv" 
                        onChange={handleExcelUpload} 
                    />
                </div>

                {/* Stats Bar */}
                {stats && !running && (
                    <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
                        <div className="stat-box"><div className="stat-num" style={{ color: '#60a5fa' }}>{stats.total}</div><div style={{ color: '#94a3b8', fontSize: '12px' }}>Total</div></div>
                        <div className="stat-box"><div className="stat-num color-found">{stats.found}</div><div style={{ color: '#94a3b8', fontSize: '12px' }}>Found</div></div>
                        <div className="stat-box"><div className="stat-num color-notfound">{stats.notfound}</div><div style={{ color: '#94a3b8', fontSize: '12px' }}>Not Found</div></div>
                        <div className="stat-box"><div className="stat-num color-error">{stats.errors}</div><div style={{ color: '#94a3b8', fontSize: '12px' }}>Errors</div></div>
                    </div>
                )}

                {/* Results Table */}
                {rows.length > 0 && (
                    <table className="hsn-table">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>GSTIN</th>
                                <th>HSN / SAC Code</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => {
                                let statusColor = '';
                                let displayStatus = row.status;

                                if (row.status === 'pending') { statusColor = 'color-pending'; displayStatus = '⏳'; }
                                else if (row.status === 'found') { statusColor = 'color-found'; displayStatus = 'found'; }
                                else if (row.status === 'notfound') { statusColor = 'color-notfound'; displayStatus = 'NOT FOUND'; }
                                else if (row.status === 'invalid') { statusColor = 'color-error'; displayStatus = 'INVALID GSTIN'; }
                                else if (row.status === 'error') { statusColor = 'color-error'; displayStatus = `❌ ${row.code}`; }

                                return (
                                    <tr key={row.gstin + idx} className="stagger-row" style={{ animationDelay: `${idx * 0.05}s` }}>
                                        <td style={{ color: '#475569' }}>{idx + 1}</td>
                                        <td style={{ color: '#f8fafc' }}>{row.gstin}</td>
                                        <td className={statusColor} style={{ fontWeight: 'bold' }}>
                                            {row.status === 'pending' ? '' : (
                                                <input
                                                    key={row.gstin + '|' + (row.code || '')}
                                                    className="code-edit"
                                                    defaultValue={row.code || ''}
                                                    placeholder="—"
                                                    title="Edit and press Enter to save to Annexure B"
                                                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                                    onBlur={e => {
                                                        const v = e.target.value.trim();
                                                        if (v !== (row.code || '')) applyManualCode(row.gstin, v);
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td className={statusColor}>{displayStatus}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Gstr2bHsnModule;
